import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import {
  getAcademicYearsDirect,
  createAcademicYearDirect,
  updateAcademicYearDirect,
  deleteAcademicYearDirect,
  publishAcademicYear,
  archiveAcademicYear,
} from '@timetable/api';
import type { AcademicYear, CreateAcademicYearRequest } from '@timetable/types';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import { ConfirmDialog } from '../ConfirmDialog';
import { YearDuplicationDialog } from './YearDuplicationDialog';
import { PrePublishValidationDialog } from './PrePublishValidationDialog';
import { queryKeys } from '../../lib/queryKeys';

interface AcademicYearFormData {
  name: string;
  start_date: string;
  end_date: string;
}

const STATUS_BADGE: Record<string, { label: string; color: 'default' | 'success' | 'warning' }> = {
  draft: { label: 'Draft', color: 'default' },
  published: { label: 'Published', color: 'success' },
  archived: { label: 'Archived', color: 'warning' },
};

export const AcademicYearManager: React.FC = () => {
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingYear, setEditingYear] = useState<AcademicYear | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [yearToDelete, setYearToDelete] = useState<AcademicYear | null>(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [yearToArchive, setYearToArchive] = useState<AcademicYear | null>(null);
  const [publishDialogYear, setPublishDialogYear] = useState<AcademicYear | null>(null);
  const [duplicationDialogOpen, setDuplicationDialogOpen] = useState(false);
  const [formData, setFormData] = useState<AcademicYearFormData>({
    name: '',
    start_date: '',
    end_date: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<AcademicYearFormData>>({});

  const { data: academicYears, isLoading } = useQuery({
    queryKey: queryKeys.academicYears.list({ school_id: schoolContext }),
    queryFn: () => getAcademicYearsDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateAcademicYearRequest) => createAcademicYearDirect(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AcademicYearFormData> }) =>
      updateAcademicYearDirect(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAcademicYearDirect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
      setDeleteConfirmOpen(false);
      setYearToDelete(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, schoolId }: { id: string; schoolId: string }) =>
      publishAcademicYear({ id, schoolId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
      setPublishDialogYear(null);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveAcademicYear({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
      setArchiveConfirmOpen(false);
      setYearToArchive(null);
    },
  });

  const handleOpenDialog = (year?: AcademicYear) => {
    if (year) {
      setEditingYear(year);
      setFormData({ name: year.name, start_date: year.start_date, end_date: year.end_date });
    } else {
      setEditingYear(null);
      setFormData({ name: '', start_date: '', end_date: '' });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingYear(null);
    setFormData({ name: '', start_date: '', end_date: '' });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Partial<AcademicYearFormData> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';
    if (!formData.end_date) errors.end_date = 'End date is required';
    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        errors.end_date = 'End date must be after start date';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm() || !schoolContext) return;
    if (editingYear) {
      updateMutation.mutate({ id: editingYear.id, data: formData });
    } else {
      createMutation.mutate({ school_id: schoolContext, ...formData });
    }
  };

  const handleDeleteClick = (year: AcademicYear) => {
    setYearToDelete(year);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (yearToDelete) deleteMutation.mutate(yearToDelete.id);
  };

  const handleArchiveClick = (year: AcademicYear) => {
    setYearToArchive(year);
    setArchiveConfirmOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (yearToArchive) archiveMutation.mutate(yearToArchive.id);
  };

  const handlePublishConfirm = () => {
    if (!publishDialogYear || !schoolContext) return;
    publishMutation.mutate({ id: publishDialogYear.id, schoolId: schoolContext });
  };

  const activeYear = academicYears?.find((year) => year.status === 'published');

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Academic Years</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => setDuplicationDialogOpen(true)}
            disabled={!academicYears || academicYears.length === 0}
          >
            Duplicate Year
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Academic Year
          </Button>
        </Stack>
      </Stack>

      {activeYear && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Published Year:</strong> {activeYear.name} ({activeYear.start_date} to{' '}
            {activeYear.end_date})
          </Typography>
        </Alert>
      )}

      {!academicYears || academicYears.length === 0 ? (
        <EmptyState
          title="No academic years found"
          description="Create your first academic year to get started"
        />
      ) : (
        <List>
          {academicYears.map((year) => {
            const badge = STATUS_BADGE[year.status] ?? STATUS_BADGE.draft;
            const isArchived = year.status === 'archived';
            return (
              <Card key={year.id} sx={{ mb: 2, opacity: isArchived ? 0.75 : 1 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box flex={1}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h6">{year.name}</Typography>
                        <Chip label={badge.label} color={badge.color} size="small" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {new Date(year.start_date).toLocaleDateString()} -{' '}
                        {new Date(year.end_date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {year.status === 'draft' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<PublishIcon />}
                          onClick={() => setPublishDialogYear(year)}
                          disabled={publishMutation.isPending}
                        >
                          Publish
                        </Button>
                      )}
                      {year.status === 'published' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<ArchiveIcon />}
                          onClick={() => handleArchiveClick(year)}
                          disabled={archiveMutation.isPending}
                        >
                          Archive
                        </Button>
                      )}
                      {!isArchived && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(year)}
                          disabled={updateMutation.isPending}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {!isArchived && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(year)}
                          disabled={deleteMutation.isPending || year.status !== 'draft'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </List>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingYear ? 'Edit Academic Year' : 'Create Academic Year'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name || 'e.g., 2025-2026'}
              fullWidth
              required
            />
            <TextField
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              error={!!formErrors.start_date}
              helperText={formErrors.start_date}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              error={!!formErrors.end_date}
              helperText={formErrors.end_date}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingYear ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Academic Year"
        message={`Are you sure you want to delete "${yearToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteConfirmOpen(false); setYearToDelete(null); }}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="error"
      />

      {/* Archive Confirmation */}
      <ConfirmDialog
        open={archiveConfirmOpen}
        title="Archive Academic Year"
        message={`Are you sure you want to archive "${yearToArchive?.name}"? Archived years are read-only and cannot be reverted.`}
        onConfirm={handleArchiveConfirm}
        onCancel={() => { setArchiveConfirmOpen(false); setYearToArchive(null); }}
        confirmLabel="Archive"
        cancelLabel="Cancel"
        severity="warning"
      />

      {/* Pre-Publish Validation Dialog */}
      {publishDialogYear && schoolContext && (
        <PrePublishValidationDialog
          open={!!publishDialogYear}
          year={publishDialogYear}
          schoolId={schoolContext}
          onConfirm={handlePublishConfirm}
          onClose={() => setPublishDialogYear(null)}
          isPublishing={publishMutation.isPending}
          publishError={publishMutation.error instanceof Error ? publishMutation.error.message : null}
        />
      )}

      {/* Year Duplication Dialog */}
      <YearDuplicationDialog
        open={duplicationDialogOpen}
        onClose={() => setDuplicationDialogOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.academicYears.all });
        }}
      />
    </Box>
  );
};
